import { useEffect, useRef, useState } from 'react';
import { toast } from '@/components/ui/toaster.jsx';
import { phoneTypeWord } from '@/lib/phone-type.js';
import { inlineSender, doorLabel, personIdentitySender } from './factWriters.js';

// ONE FACT ROW: label · value (or the state word) · one verb. CLIENT-RECORD-SPEC
// §5 + the canvas rulings of 2026-09-16:
//   - an empty required fact is a FIELD IN PLACE (dropdown, text, date,
//     number), not a red icon; it posts through the one writer named by
//     `fact.writer` and the record re-reads (onSaved)
//   - red only for "blocks the application" (required and not known); amber
//     only for a hole (somebody had the form open and left it blank)
//   - source and tier are quiet text at the right, never a chip per cell
//   - a state word is never an empty cell: never / pending / left blank / could not check
// Nothing here derives state; it renders what the record said.

export const STATE_WORDS = { never: 'not recorded', pending: 'pending', hole: 'left blank', unread: 'could not check' };
const REGISTRY_CONFIRMED_WORDS = 'This name is confirmed against the FAA airmen registry. Change it on the person sheet, with a reason.';

const inputCls = 'min-h-[36px] rounded-card border border-border bg-bg px-2.5 py-1 text-sm text-text outline-none focus:border-accent';

export function Editor({ kind, options, value, onCommit, onCancel, autoFocus }) {
  const [v, setV] = useState(value ?? '');
  const ref = useRef(null);
  useEffect(() => { if (autoFocus && ref.current) ref.current.focus(); }, [autoFocus]);
  const commit = () => onCommit(v);
  const keys = (e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } if (e.key === 'Escape') onCancel(); };
  if (kind === 'select') {
    return (
      <select ref={ref} className={inputCls} value={v} onChange={(e) => { setV(e.target.value); onCommit(e.target.value); }} onKeyDown={keys} aria-label="value">
        <option value="">Choose</option>
        {/* A stored value that is not on the list is shown as it is, to be replaced, never guessed onto it (David 2026-11-04). */}
        {v && !(options || []).some((o) => (o && typeof o === 'object' ? o.value : o) === v) ? <option value={v} disabled>{`${v} (not on the list: pick the right one)`}</option> : null}
        {(options || []).map((o) => (o && typeof o === 'object'
          ? <option key={o.value} value={o.value}>{o.label}</option>
          : <option key={o} value={o}>{o}</option>))}
      </select>
    );
  }
  return (
    <input ref={ref} className={`${inputCls} ${kind === 'number' || kind === 'money' ? 'w-36 font-mono' : kind === 'date' ? 'w-44 font-mono' : 'w-64'}`}
      type={kind === 'date' ? 'date' : 'text'} inputMode={kind === 'number' || kind === 'money' ? 'decimal' : undefined}
      value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={keys} aria-label="value" />
  );
}

// A person's legal name, as its parts (people-create-door C5). Seeded from the
// record's parts; Enter or Save commits, Escape cancels.
const NAME_PARTS = [['first_name', 'First name', 'w-36'], ['middle_name', 'Middle name', 'w-28'], ['last_name', 'Last name', 'w-40']];
export function NameEditor({ parts, onCommit, onCancel }) {
  const [p, setP] = useState(Object.fromEntries(NAME_PARTS.map(([k]) => [k, parts?.[k] || ''])));
  const keys = (e) => { if (e.key === 'Enter') { e.preventDefault(); onCommit(p); } if (e.key === 'Escape') onCancel(); };
  return (
    <div className="flex flex-wrap items-center gap-1.5" onKeyDown={keys}>
      {NAME_PARTS.map(([k, l, w], i) => (
        <input key={k} className={`${inputCls} ${w}`} placeholder={l} value={p[k]} onChange={(e) => setP((x) => ({ ...x, [k]: e.target.value }))} aria-label={l} autoFocus={i === 0} />
      ))}
      <button type="button" className="min-h-[36px] rounded-card border border-accent bg-accent px-3 text-sm font-medium text-white" onClick={() => onCommit(p)}>Save</button>
      <button type="button" className="min-h-[36px] px-2 text-sm text-text-muted hover:text-text" onClick={onCancel}>Cancel</button>
    </div>
  );
}

// The sparse name body: only the parts that differ from the record's.
function changedParts(before, after) {
  const body = {};
  for (const [k] of NAME_PARTS) { const v = String(after?.[k] ?? '').trim(); if (v !== (before?.[k] || '')) body[k] = v; }
  return body;
}

export function AddressEditor({ parts, onCommit, onCancel }) {
  const [p, setP] = useState({ street: parts?.street || '', street_2: parts?.street_2 || '', city: parts?.city || '', state: parts?.state || '', zip: parts?.zip || '' });
  const set = (k) => (e) => setP((x) => ({ ...x, [k]: e.target.value }));
  const keys = (e) => { if (e.key === 'Enter') { e.preventDefault(); onCommit(p); } if (e.key === 'Escape') onCancel(); };
  return (
    <div className="flex flex-wrap items-center gap-1.5" onKeyDown={keys}>
      <input className={`${inputCls} w-56`} placeholder="Street" value={p.street} onChange={set('street')} aria-label="Street" autoFocus />
      <input className={`${inputCls} w-36`} placeholder="City" value={p.city} onChange={set('city')} aria-label="City" />
      <input className={`${inputCls} w-16 uppercase`} placeholder="ST" maxLength={2} value={p.state} onChange={set('state')} aria-label="State" />
      <input className={`${inputCls} w-24 font-mono`} placeholder="ZIP" value={p.zip} onChange={set('zip')} aria-label="ZIP" />
      <button type="button" className="min-h-[36px] rounded-card border border-accent bg-accent px-3 text-sm font-medium text-white" onClick={() => onCommit(p)}>Save</button>
      <button type="button" className="min-h-[36px] px-2 text-sm text-text-muted hover:text-text" onClick={onCancel}>Cancel</button>
    </div>
  );
}

function fmtValue(field, value) {
  if (value == null || value === '') return null;
  if (field === 'hull_value' || field === 'deductible_in_motion' || field === 'deductible_not_in_motion') {
    const n = Number(value); if (Number.isFinite(n)) return '$' + n.toLocaleString('en-US');
  }
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

const MONO = new Set(['hull_value', 'deductible_in_motion', 'deductible_not_in_motion', 'date_of_birth', 'medical_date', 'last_flight_review_date', 'basicmed_course_date', 'total_flight_hours', 'hours_last_12_months', 'year', 'seats', 'serial_number', 'airport', 'fein']);

// gatedBy: a sentence naming the fact that has to be set first. The row then
// renders read-only (value or state word plus that sentence) and posts nothing:
// the address door refuses while the account has no policyholder type (1c,
// DISPATCH-PREFLIGHT-SPEC §1), so the record asks for the type first rather
// than letting the address row fail on a rule it cannot satisfy.
export default function FactRow({ field, fact, label, kind = 'text', options, accountId, accountName, onSaved, onOpenDoor, lit = true, showMeta = false, hasOpenTerm = false, gatedBy = null }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  if (!fact) return null;
  const known = fact.state === 'known';
  const required = !!fact.required;
  const blocks = (fact.blocks || []).length > 0;
  // A person's name posts as its parts through the person door's sender (never
  // one string split on a guess), and only the parts that changed.
  const nameRow = field === 'name' && fact.writer?.door === 'person_identity';
  const sender = gatedBy ? null : inlineSender({ writer: fact.writer, accountId, accountName });
  const nameSender = gatedBy || !nameRow ? null : personIdentitySender(fact.writer);
  const post = nameSender ? (parts) => nameSender(changedParts(fact.parts, parts)) : sender;
  const door = doorLabel(fact.writer, { hasOpenTerm });
  const opts = options || fact.options || null;
  const editorKind = field === 'address' ? 'address' : nameRow ? 'name' : (opts ? 'select' : kind);
  const red = required && !known;
  const amber = !required && fact.state === 'hole';
  const dot = red ? <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-danger" aria-label="required" /> : amber ? <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-warning" aria-label="left blank" /> : null;

  async function commit(value, reason) {
    if (!post) return;
    if (editorKind === 'name' && Object.keys(changedParts(fact.parts, value)).length === 0) { setEditing(false); return; }
    if (editorKind !== 'address' && editorKind !== 'name' && String(value ?? '').trim() === '') { setEditing(false); return; }
    setSaving(true);
    try {
      const res = await post(value, reason);
      if (res && res.success === false) throw new Error(res.error || 'Save failed');
      for (const w of (res?.warnings || [])) toast.info(typeof w === 'string' ? w : (w.message || String(w)));
      toast.success(`${label || field} saved`);
      setEditing(false);
      onSaved?.();
    } catch (err) {
      // A name confirmed against the FAA registry needs a reason to change
      // (AR13); the reason lives on the person sheet, so the row opens it.
      if (err?.body?.code === 'registry_confirmed') { toast.info(REGISTRY_CONFIRMED_WORDS); setEditing(false); onOpenDoor?.(fact.writer); }
      else toast.error(err?.message || 'Save failed');
    } finally { setSaving(false); }
  }

  const empty = !known;
  // A row the read could not answer (unread) is never an input in place: typing
  // over a value nobody could read is a write on a guess. It opens the person.
  const showEditor = !gatedBy && (editing || (empty && fact.state !== 'unread' && !!post && lit));
  // The record names the address parts it found blank (client-record.service
  // sets fact.missing from the one completeness predicate). The row renders
  // the list; it derives nothing. A state nothing reads does not exist.
  const missingNote = field === 'address' && !known && Array.isArray(fact.missing) && fact.missing.length
    ? <span className="text-xs text-text-dim" data-missing>missing {fact.missing.join(', ')}</span>
    : null;
  return (
    <div className={`grid min-h-[44px] grid-cols-[160px_minmax(0,1fr)_auto] items-center gap-3 border-b border-border py-1.5 last:border-b-0 ${lit ? '' : 'opacity-40'}`} data-fact={field} data-state={fact.state}>
      <div className="text-sm text-text-muted">{label || field}</div>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {showEditor ? (
          editorKind === 'address'
            ? <><AddressEditor parts={fact.parts} onCommit={commit} onCancel={() => setEditing(false)} />{missingNote}</>
            : editorKind === 'name'
            ? <><NameEditor parts={fact.parts} onCommit={commit} onCancel={() => setEditing(false)} />{saving ? <span className="text-xs text-text-dim">saving</span> : null}</>
            : <><span className="flex items-center">{dot}<Editor kind={editorKind} options={opts} value={known ? fact.value : ''} onCommit={commit} onCancel={() => setEditing(false)} autoFocus={editing} /></span>{saving ? <span className="text-xs text-text-dim">saving</span> : null}{door ? <span className="text-xs text-text-dim">{door}</span> : null}</>
        ) : gatedBy ? (
          <span className="flex items-center text-sm" data-gated>
            {dot}
            {known ? <span className={`text-text ${MONO.has(field) ? 'font-mono' : ''}`}>{fmtValue(field, fact.value)}</span> : <span className="text-text-dim" data-state-word>{STATE_WORDS[fact.state] || fact.state}</span>}
            <span className="ml-2 text-xs text-text-dim">{gatedBy}</span>
          </span>
        ) : known ? (
          <button type="button" className={`text-left text-sm text-text hover:underline ${MONO.has(field) ? 'font-mono' : ''}`} onClick={() => post ? setEditing(true) : onOpenDoor?.(fact.writer)} title={post ? 'Edit' : (door || 'Open')} data-verb="edit">
            {fmtValue(field, fact.value)}{fact.detail ? <span className="ml-2 text-xs text-text-dim">{fact.detail}</span> : null}
            {field === 'phone' && fact.phone_type ? <span className="ml-2 font-sans text-xs text-text-dim" data-phone-type>{phoneTypeWord(fact.phone_type)}</span> : null}
          </button>
        ) : (
          <span className="flex items-center text-sm text-text-dim">
            {dot}
            <span data-state-word>{STATE_WORDS[fact.state] || fact.state}</span>
            {missingNote ? <span className="ml-2">{missingNote}</span> : null}
            {fact.writer?.door === 'roster' || fact.writer?.door === 'phf_form' || (fact.writer && (!post || fact.state === 'unread')) ? (
              <button type="button" className="ml-2 text-xs text-text-muted underline-offset-2 hover:text-text hover:underline" onClick={() => onOpenDoor?.(fact.writer)} data-verb="door">{door || (fact.state === 'unread' ? 'Open' : 'Set')}</button>
            ) : null}
          </span>
        )}
        {blocks && known ? <span className="text-[11px] text-danger">blocks the application</span> : null}
      </div>
      <div className="text-right text-[11px] text-text-dim">
        {showMeta && known ? [fact.source, fact.tier].filter(Boolean).join(' · ') : null}
        {showMeta && known && fact.at ? <span className="ml-1">{String(fact.at).slice(0, 10)}</span> : null}
      </div>
    </div>
  );
}
